import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Bot, Brain, Network, Mic, Building2, Cog, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Profile image served from same domain to avoid security software blocking
const PROFILE_IMAGE = "/profile-image.jpg";

const expertisePreview = [
{ icon: Bot, title: "Agentic AI Architecture", desc: "Building autonomous AI systems that think and act" },
{ icon: Brain, title: "Generative AI Strategy", desc: "Enterprise-ready Gen AI implementation" },
{ icon: Network, title: "LLM/RAG Systems", desc: "Production-grade retrieval augmented generation" },
{ icon: Mic, title: "Voice AI & Routing", desc: "Predictive voice automation solutions" },
{ icon: Building2, title: "Contact Center AI", desc: "Modernizing customer experience" },
{ icon: Cog, title: "ML Automation", desc: "Scalable machine learning pipelines" }];


const servicesPreview = [
{ title: "Contact Center Modernization", desc: "Transform legacy contact centers with Gen AI and Agentic AI capabilities" },
{ title: "Voice AI Implementation", desc: "Deploy predictive routing and intelligent voice automation" },
{ title: "AI Strategy Consulting", desc: "Roadmap development for enterprise AI transformation" }];


export default function HomePage() {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Successfully subscribed to the newsletter!");
      setEmail("");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to subscribe";
      toast.error(message);
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-[90vh] flex items-center hero-gradient grain" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}>

              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-6">
                AI Strategist & Enterprise Architect
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-zinc-950 leading-tight mb-6">
                Enterprise AI, Agentic Systems, and Contact Center Modernization — Made Practical.
              </h1>
              <p className="md:text-xl max-w-xl !text-lg mb-8 text-zinc-600">Insights on practical Gen AI, Contact Center Voice AI, and ML Automation adoption.

              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/blog">
                  <Button
                    className="bg-royal-blue-800 hover:bg-royal-blue-700 text-white px-8 py-6 text-xs tracking-wider uppercase"
                    data-testid="hero-cta-articles">

                    Read Articles
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </Link>
                <Link to="/newsletter">
                  <Button
                    variant="outline"
                    className="border-royal-blue-800 text-royal-blue-800 hover:bg-royal-blue-800 hover:text-white px-8 py-6 text-xs tracking-wider uppercase"
                    data-testid="hero-cta-newsletter">

                    Subscribe to Newsletter
                  </Button>
                </Link>
                <Link to="/lets-talk-ai">
                  <Button
                    variant="ghost"
                    className="text-zinc-600 hover:text-zinc-950 px-8 py-6 text-xs tracking-wider uppercase"
                    data-testid="hero-cta-services">

                    Let's Talk AI
                    <ChevronRight size={16} />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Content - Profile Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center lg:justify-end">

              <div className="relative">
                <div className="absolute -inset-4 bg-royal-blue-800/5 rounded-sm"></div>
                <img
                  src={PROFILE_IMAGE}
                  alt="Vasanth - AI Strategist"
                  className="relative profile-image w-72 h-72 md:w-96 md:h-96 object-cover animate-float"
                  data-testid="hero-profile-image" />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expertise Preview Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="expertise-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-16">

            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Core Expertise
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-4">
              Deep Technical & Strategic Expertise
            </h2>
            <p className="max-w-2xl !text-lg text-zinc-600">Two decades of experience in AI/ML, Enterprise Architecture, and Digital Transformation.

            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertisePreview.map((item, index) =>
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="card-hover p-8 bg-zinc-50 border border-zinc-200 rounded-sm"
              data-testid={`expertise-card-${index}`}>

                <div className="skill-icon mb-6">
                  <item.icon size={24} />
                </div>
                <h3 className="font-display text-xl font-semibold text-zinc-950 mb-2">
                  {item.title}
                </h3>
                <p className="text-zinc-600">
                  {item.desc}
                </p>
              </motion.div>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link to="/skills">
              <Button
                variant="outline"
                className="border-zinc-300 text-zinc-700 hover:border-royal-blue-800 hover:text-royal-blue-800"
                data-testid="view-all-skills-btn">

                View All Skills
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Let's Talk AI Preview Section */}
      <section className="py-24 md:py-32 section-muted" data-testid="lets-talk-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-16">

            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Let's Talk AI
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-4">
              Topics I Love Discussing
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {servicesPreview.map((service, index) =>
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="service-card rounded-sm"
              data-testid={`topic-preview-${index}`}>

                <h3 className="font-display text-xl font-semibold text-zinc-950 mb-3">
                  {service.title}
                </h3>
                <p className="text-zinc-600 mb-6">
                  {service.desc}
                </p>
                <Link
                to="/lets-talk-ai"
                className="inline-flex items-center text-sm font-medium text-royal-blue-800 hover:text-royal-blue-700">

                  Learn more
                  <ChevronRight size={16} />
                </Link>
              </motion.div>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link to="/lets-talk-ai">
              <Button
                className="bg-royal-blue-800 hover:bg-royal-blue-700 text-white px-8 py-6 text-xs tracking-wider uppercase"
                data-testid="explore-topics-btn">

                Explore All Topics
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="py-24 md:py-32 cta-section" data-testid="newsletter-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto text-center">

            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-4">
              Stay Ahead in AI
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Subscribe to my newsletter for insights on Agentic AI, Contact Center AI, and Enterprise AI Strategy.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200 focus:border-white"
                data-testid="newsletter-email-input" />

              <Button
                type="submit"
                disabled={isSubscribing}
                className="bg-white text-royal-blue-800 hover:bg-blue-50 px-8"
                data-testid="newsletter-submit-btn">

                {isSubscribing ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}>

              <img
                src={PROFILE_IMAGE}
                alt="Vasanth"
                className="w-full max-w-md mx-auto lg:mx-0 rounded-sm shadow-lg" />

            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}>

              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
                About
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 mb-6">
                Two Decades of Enterprise Innovation
              </h2>
              <p className="!text-lg mb-6 text-zinc-600">From Senior VP to Director, I’ve led transformative AI initiatives across Fortune 500 enterprises, shaping how large organizations adopt and scale next‑generation intelligence. My expertise spans AI/ML, Voice AI, Agentic Systems, and Enterprise Architecture, giving me a rare blend of deep technical fluency and strategic leadership


              </p>
              <p className="text-zinc-600 leading-relaxed mb-8">With advanced education from Johns Hopkins (Agentic AI), UT Austin (AI/ML), Rutgers (MBA – Data Science) and certifications in AWS, CGAI, TOGAF, and Neo4j, I bring a powerful combination of engineering rigor, architectural discipline, and business insight to every engagement. I specialize in turning complex AI capabilities into practical, production‑ready solutions—from modernizing contact centers and designing LLM/RAG architectures to building agentic workflows that drive measurable impact. My work bridges the gap between innovation and execution, helping leaders adopt AI with clarity, confidence, and long‑term scalability.
Across every project, my focus remains consistent:
deliver architectures that are resilient, explainable, and built for real‑world enterprise environments
              </p>
              <Link to="/about">
                <Button
                  variant="outline"
                  className="border-royal-blue-800 text-royal-blue-800 hover:bg-royal-blue-800 hover:text-white px-8 py-6 text-xs tracking-wider uppercase"
                  data-testid="learn-more-about-btn">

                  Learn More About Me
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>);

}